import { SectionMessageAction } from '@atlaskit/section-message';
import { AxiosError } from 'axios';
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ErrorMessage } from '../components/ErrorMessage';
import { LoadingPage } from '../components/LoadingPage';
import { togglApi } from '../services/togglApi';
import {
  TogglClient,
  TogglProject,
  TogglProjectExtended,
  TogglTag,
  TogglTask,
  TogglWorkspace,
} from '../types/toggl-api';
import { useTogglAccountContext } from './ToggleAccountTokenProvider';

interface TogglDataProviderContextData {
  id: number;
  projects: TogglProjectExtended[];
  tags?: TogglTag[];
  tasks?: TogglTask[];
  workspaces: TogglWorkspace[];
  timezone: string;
  default_workspace_id: number;
}

const TogglDataProviderContext = React.createContext<
  TogglDataProviderContextData | undefined
>(undefined);

enum TogglApiErrorReason {
  UNKNOWN,
  NOT_FOUND,
  FORBIDDEN,
}

const extendProjectData = (project: TogglProject, clients: TogglClient[]) => {
  const matchingClient =
    project.client_id !== null &&
    clients.find((client) => client.id === project.client_id);
  const displayName = matchingClient
    ? `${project.name} (${matchingClient.name})`
    : project.name;
  return { ...project, displayName };
};

const getErrorExplanation = (error: TogglApiErrorReason) => {
  switch (error) {
    case TogglApiErrorReason.NOT_FOUND:
      return "Couldn't find Toggl account matching saved api token.";
    case TogglApiErrorReason.FORBIDDEN:
      return 'Configured Toggle API token is invalid.';
    default:
      return 'An error occurred while attempting to retrieve Toggl account data.';
  }
};

export function TogglDataProvider({ children }: Readonly<PropsWithChildren>) {
  const { token } = useTogglAccountContext();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<TogglApiErrorReason | undefined>();
  const [context, setContext] = useState<TogglDataProviderContextData>();
  const { removeToken } = useTogglAccountContext();

  useEffect(() => {
    void (async () => {
      try {
        const {
          id,
          workspaces,
          projects,
          tasks,
          tags,
          timezone,
          default_workspace_id,
          clients,
        } = await togglApi(token).getTogglData();
        const activeProjects =
          projects
            ?.filter((p) => p.active)
            .map((p) => extendProjectData(p, clients)) ?? [];
        const activeTasks = tasks?.filter((task) => task.active);
        setContext({
          id,
          workspaces,
          projects: activeProjects,
          tasks: activeTasks,
          tags,
          timezone,
          default_workspace_id,
        });
      } catch (e) {
        console.error(e);
        if ((e as AxiosError)?.response?.status === 404) {
          setError(TogglApiErrorReason.NOT_FOUND);
        } else if ((e as AxiosError)?.response?.status === 403) {
          setError(TogglApiErrorReason.FORBIDDEN);
        } else {
          setError(TogglApiErrorReason.UNKNOWN);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const resetToken = useCallback(async () => {
    setIsLoading(true);
    await removeToken();
    setIsLoading(false);
  }, [setIsLoading]);

  const value = useMemo(() => context, [context]);
  if (isLoading) {
    return <LoadingPage />;
  } else if (error !== undefined) {
    return (
      <ErrorMessage
        title="Error retrieving Toggl data"
        text={getErrorExplanation(error)}
        actions={
          error === TogglApiErrorReason.NOT_FOUND ||
          error === TogglApiErrorReason.FORBIDDEN
            ? [
                <SectionMessageAction
                  key="reset_token"
                  onClick={() => void resetToken()}
                >
                  Disconnect account
                </SectionMessageAction>,
              ]
            : []
        }
      />
    );
  } else {
    return (
      <TogglDataProviderContext.Provider value={value}>
        {children}
      </TogglDataProviderContext.Provider>
    );
  }
}

export function useTogglDataContext() {
  const context = useContext(TogglDataProviderContext);
  if (!context) {
    throw new Error(
      'Toggl Data provider context not initialized, probably context provider is missing'
    );
  }
  return context;
}
